import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  ANALYTICS_CONSENT_EVENT,
  getAnalyticsConsent,
  setAnalyticsConsent,
} from "@/lib/analytics";

const preferenceLabel = {
  accepted: "Analytics accepted",
  anonymous: "Anonymous statistics only",
  rejected: "All analytics rejected",
};

export default function Privacy() {
  const [consent, setConsent] = useState(getAnalyticsConsent);

  useEffect(() => {
    const update = (event) => setConsent(event.detail);
    window.addEventListener(ANALYTICS_CONSENT_EVENT, update);
    return () => window.removeEventListener(ANALYTICS_CONSENT_EVENT, update);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="page-shell max-w-4xl py-10 md:py-14">
        <header className="border-b border-border pb-8">
          <div className="flex items-center gap-2 text-primary">
            <ShieldCheck className="h-5 w-5" />
            <p className="text-xs font-black uppercase tracking-[0.25em]">Privacy</p>
          </div>
          <h1 className="mt-3 font-display text-3xl font-black md:text-5xl">Privacy and analytics</h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-muted-foreground md:text-base">
            How NamasteNetwork.ai uses optional website analytics and how you control it.
          </p>
        </header>

        <div className="space-y-8 py-8 text-sm leading-7 text-muted-foreground md:text-base">
          <section>
            <h2 className="font-display text-xl font-black text-foreground">Information we handle</h2>
            <p className="mt-2">
              We process account identity and contact details, profile preferences, organiser and
              creator information, event submissions, bookings, ticket and check-in records,
              payment status, support messages, device push tokens and security logs needed to run
              the service. Card details are handled by Stripe and are not stored by NamasteNetwork.ai.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-black text-foreground">How we use information</h2>
            <p className="mt-2">
              We use this information to provide accounts, event discovery, bookings, QR tickets,
              check-in, organiser reporting, notifications, fraud prevention, support and legal or
              accounting records. Where you opt in, profile preferences may also tailor event and
              campaign recommendations.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-black text-foreground">Service providers and sharing</h2>
            <p className="mt-2">
              We share only what is needed with service providers such as Stripe for payments,
              Apple and Google for sign-in and wallet features, Expo for mobile notifications,
              Brevo for opted-in communications and Google Analytics for consented website
              measurement. Event organisers receive booking and attendance information needed to
              operate their event. We do not sell personal information.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-black text-foreground">Google Analytics</h2>
            <p className="mt-2">
              With your permission, we use Google Analytics 4 to measure page visits, navigation,
              approximate location, device and browser information, outbound links and general site
              engagement. We do not intentionally send names, email addresses, telephone numbers,
              payment details or ticket QR contents to Google Analytics.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-black text-foreground">Anonymous first-party statistics</h2>
            <p className="mt-2">
              Unless you object, we record the page route, date and broad device, operating-system
              and browser categories in our own service so administrators can improve the website.
              We do not store your IP address, full browser signature or a visitor identifier in
              these records. Internal team IP addresses are excluded and results are shown only as
              aggregate statistics. Choose Reject all below to opt out at any time.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-black text-foreground">Retention and account deletion</h2>
            <p className="mt-2">
              Profile data is kept while your account is active. You can delete your account from
              the mobile Profile screen. We then remove the account and personal profile data and
              anonymise booking ownership. Limited transaction and event records may be retained
              for accounting, fraud prevention, dispute handling and legal obligations.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-black text-foreground">Your rights</h2>
            <p className="mt-2">
              Depending on where you live, you may ask to access, correct, delete or restrict use
              of your information, object to processing or request a portable copy. You may also
              withdraw optional consent at any time. Contact us to make a request or complain to
              your local data protection authority.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-black text-foreground">Children and international processing</h2>
            <p className="mt-2">
              The service is not intended for children to create accounts independently. Parents
              or guardians should manage family bookings. Some providers may process information
              outside the UK; where required, appropriate contractual safeguards are used.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-black text-foreground">Your choice</h2>
            <p className="mt-2">
              Anonymous first-party statistics operate by default under the statistical-purpose
              exception and can be disabled with Reject all. Google Analytics remains off until you
              explicitly accept it. Your choice does not affect event discovery, booking, account
              access or any other core feature and can be changed below at any time.
            </p>
            <p className="mt-3 font-bold text-foreground" aria-live="polite">
              Current preference: {preferenceLabel[consent] || "Not selected"}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={() => setAnalyticsConsent("accepted")}>Accept analytics</Button>
              <Button variant="outline" onClick={() => setAnalyticsConsent("anonymous")}>Anonymous only</Button>
              <Button variant="outline" onClick={() => setAnalyticsConsent("rejected")}>Reject all</Button>
            </div>
          </section>

          <section>
            <h2 className="font-display text-xl font-black text-foreground">Google processing</h2>
            <p className="mt-2">
              Google acts as our analytics service provider and may process analytics information on
              its infrastructure. For more information, read Google&apos;s explanation of how it uses
              information from sites and apps that use its services.
            </p>
            <a
              href="https://policies.google.com/technologies/partner-sites"
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-block font-bold text-primary underline underline-offset-4"
            >
              How Google uses information
            </a>
          </section>

          <section>
            <h2 className="font-display text-xl font-black text-foreground">Contact</h2>
            <p className="mt-2">
              Questions about this notice can be sent to
              {" "}<a href="mailto:namastenetwork.ai@gmail.com" className="font-bold text-primary underline underline-offset-4">namastenetwork.ai@gmail.com</a>.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
